import { subscribe, getState }  from '../store';
import { socket }               from '../socket';
import { registerScreen }       from '../router';
import { toast, escHtml }       from '../ui';
import type { Player }          from '../../../shared/types';

// ─── Template ────────────────────────────────────────────────────────────────

function render(): string {
  return `
    <div class="lobby__header">
      <h1 class="lobby__title">Your Room</h1>
      <button class="lobby__settings" id="lobby-settings" aria-label="Settings">⚙️</button>
    </div>

    <!-- Room code -->
    <div class="card lobby__code-card">
      <p class="lobby__code-label">Room Code</p>
      <p class="lobby__code" id="lobby-code">—</p>
      <div class="lobby__code-actions">
        <button class="lobby__code-btn" id="lobby-copy">📋 Copy</button>
        <button class="lobby__code-btn" id="lobby-share">📤 Share</button>
      </div>
    </div>

    <!-- QR code -->
    <div class="lobby__qr">
      <div class="lobby__qr-box" id="lobby-qr-box">
        <svg id="lobby-qr-svg" viewBox="0 0 100 100"></svg>
      </div>
      <span class="lobby__qr-label">Tap to enlarge · Scan to join</span>
    </div>

    <!-- QR modal -->
    <div class="qr-modal" id="qr-modal">
      <div class="qr-modal__box">
        <svg id="qr-modal-svg" viewBox="0 0 100 100" style="width:100%;height:100%"></svg>
      </div>
      <button class="qr-modal__close" id="qr-modal-close">Tap anywhere to close</button>
    </div>

    <!-- Players -->
    <div class="lobby__players-header">
      <span class="lobby__players-label">Players</span>
      <span class="lobby__players-count" id="lobby-player-count">0 / 12</span>
    </div>
    <div class="lobby__players-list" id="lobby-players"></div>

    <!-- Footer -->
    <div class="lobby__footer">
      <p class="lobby__waiting" id="lobby-waiting" style="display:none">
        Waiting for more players…
      </p>
      <button class="btn btn--primary" id="lobby-start" disabled>
        Start Game 🌋
      </button>
    </div>
  `;
}

// ─── Screen ──────────────────────────────────────────────────────────────────

let unsubscribe: (() => void) | null = null;

registerScreen('lobby', {
  mount() {
    const el = document.getElementById('screen-lobby')!;
    el.innerHTML = render();

    const { session, roomCode } = getState();
    if (roomCode) setCode(roomCode);
    if (session)  updatePlayers(session.players);

    // Copy button
    document.getElementById('lobby-copy')!.addEventListener('click', async () => {
      const code = getState().roomCode ?? '';
      await navigator.clipboard.writeText(code);
      toast('Code copied!', 'success');
    });

    // Share button
    document.getElementById('lobby-share')!.addEventListener('click', async () => {
      const code = getState().roomCode ?? '';
      const text = `Join my Lava room! Code: ${code}`;
      if (navigator.share) {
        await navigator.share({ title: 'Join Lava', text });
      } else {
        await navigator.clipboard.writeText(text);
        toast('Link copied!', 'success');
      }
    });

    // QR tap → fullscreen
    document.getElementById('lobby-qr-box')!.addEventListener('click', () => {
      const modal = document.getElementById('qr-modal')!;
      const svg   = document.getElementById('qr-modal-svg')!;
      svg.innerHTML = document.getElementById('lobby-qr-svg')!.innerHTML;
      modal.classList.add('open');
    });

    document.getElementById('qr-modal')!.addEventListener('click', () => {
      document.getElementById('qr-modal')!.classList.remove('open');
    });

    // Start button
    const startBtn = document.getElementById('lobby-start') as HTMLButtonElement;
    startBtn.addEventListener('click', () => {
      socket.send({ type: 'host_start' });
      startBtn.disabled     = true;
      startBtn.textContent  = 'Starting…';
    });

    // Reactive updates — read myPlayer from live state each time
    unsubscribe = subscribe(({ session: s, roomCode: rc, myPlayer }) => {
      if (rc) setCode(rc);
      if (!s) return;

      updatePlayers(s.players);

      const waitEl   = document.getElementById('lobby-waiting')!;
      const isHost   = !!myPlayer?.isHost;
      const count    = Object.keys(s.players).length;
      const canStart = count >= 2;

      if (isHost) {
        startBtn.style.display = '';
        startBtn.disabled      = !canStart;
        waitEl.style.display   = canStart ? 'none' : 'block';
        waitEl.textContent     = 'Waiting for players… (min 2)';
      } else {
        startBtn.style.display = 'none';
        waitEl.style.display   = 'block';
        waitEl.textContent     = 'Waiting for host to start…';
      }
    });
  },

  unmount() {
    unsubscribe?.();
    unsubscribe = null;
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

function setCode(roomCode: string): void {
  const el = document.getElementById('lobby-code');
  if (el) el.textContent = roomCode;
  renderQr(roomCode);
}

function updatePlayers(players: Record<string, Player>): void {
  const list   = document.getElementById('lobby-players');
  const count  = document.getElementById('lobby-player-count');
  if (!list || !count) return;

  const sorted = Object.values(players).sort((a, b) => a.joinOrder - b.joinOrder);
  count.textContent = `${sorted.length} / 12`;

  // Only re-render players that changed — add new, keep existing
  const existingIds = new Set(
    Array.from(list.querySelectorAll<HTMLElement>('[data-player-id]'))
      .map(el => el.dataset.playerId!)
  );

  for (const player of sorted) {
    if (!existingIds.has(player.id)) {
      list.appendChild(buildPlayerRow(player));
    }
  }

  // Remove players who left
  for (const el of Array.from(list.querySelectorAll<HTMLElement>('[data-player-id]'))) {
    if (!players[el.dataset.playerId!]) el.remove();
  }
}

function buildPlayerRow(player: Player): HTMLElement {
  const row = document.createElement('div');
  row.className = 'lobby__player';
  row.dataset.playerId = player.id;

  const icon  = player.isHost ? '👑' : '🟠';
  const badge = player.isHost ? 'Host' : 'Player';

  row.innerHTML = `
    <span class="lobby__player__icon">${icon}</span>
    <span class="lobby__player__name">${escHtml(player.username)}</span>
    <span class="lobby__player__badge">${badge}</span>
  `;
  return row;
}

// ─── QR generator (pure SVG, no library) ─────────────────────────────────────

function renderQr(text: string): void {
  // Minimal placeholder QR pattern — replace with a real QR lib later
  // Uses a simple visual placeholder so the UI isn't broken before lib integration
  const svg = document.getElementById('lobby-qr-svg');
  if (!svg) return;

  const size  = 100;
  const cells = 10;
  const cell  = size / cells;

  // Deterministic "pattern" from text so it looks unique per room
  let hash = 0;
  for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) >>> 0;

  let rects = '';
  // Corner markers
  rects += qrCorner(0, 0, cell);
  rects += qrCorner((cells - 7) * cell, 0, cell);
  rects += qrCorner(0, (cells - 7) * cell, cell);

  // Data cells (pseudo-random based on room code)
  for (let r = 0; r < cells; r++) {
    for (let c = 0; c < cells; c++) {
      if (isCornerArea(r, c, cells)) continue;
      const bit = (hash >> ((r * cells + c) % 32)) & 1;
      if (bit) rects += `<rect x="${c * cell}" y="${r * cell}" width="${cell}" height="${cell}" fill="#000"/>`;
    }
  }

  svg.innerHTML = `<rect width="100" height="100" fill="#fff"/>${rects}`;
}

function qrCorner(x: number, y: number, cell: number): string {
  return `
    <rect x="${x}" y="${y}" width="${cell * 7}" height="${cell * 7}" fill="#000"/>
    <rect x="${x + cell}" y="${y + cell}" width="${cell * 5}" height="${cell * 5}" fill="#fff"/>
    <rect x="${x + cell * 2}" y="${y + cell * 2}" width="${cell * 3}" height="${cell * 3}" fill="#000"/>
  `;
}

function isCornerArea(r: number, c: number, cells: number): boolean {
  return (r < 7 && c < 7) || (r < 7 && c >= cells - 7) || (r >= cells - 7 && c < 7);
}
